import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Flow thresholds kept in sync with the Channel Checker ON/OFF rules.
const MIN_FLOW_THRESHOLD = 0.05;
const GENSET_ACTIVE_THRESHOLD = 0.5;

// Categories that consume (or are structural) — everything else on the tree is a
// supply node. Deriving sources by exclusion means a new supply category added in
// the DB (WIND, BATTERY, UPS, …) shows up here with no frontend change.
const CONSUMER_CATEGORIES = new Set(["MACHINE", "LOAD", "VIRTUAL", "ENVIRONMENT"]);

// Known supply categories get their established colour/icon. `signed` marks the
// ones where a negative reading means export; SOLAR/GENERATOR readings carry CT
// polarity noise, so the rest of the app takes their magnitude and so do we.
const CATEGORY_STYLES = {
  GRID: { label: "Grid", color: "#f39c12", icon: "/images/dashboard/tower.svg", signed: true },
  MAIN: { label: "Main", color: "#f39c12", icon: "/images/dashboard/tower.svg", signed: true },
  SOLAR: { label: "Solar", color: "#2ecc71", icon: "/images/dashboard/solar.svg", signed: false },
  GENERATOR: {
    label: "Generator",
    color: "#e74c3c",
    icon: "/images/dashboard/generator.svg",
    signed: false,
  },
};
// Colours handed to supply categories we don't know about yet.
const FALLBACK_COLORS = ["#8b5cf6", "#0ea5e9", "#ec4899", "#14b8a6", "#6366f1", "#f472b6"];
// Left-to-right placement preference; anything unknown is appended after these.
const ARC_PREFERENCE = ["SOLAR", "GRID", "MAIN", "GENERATOR"];
// Grouping order in the selector panel (mains first — they are the branch feed).
const PANEL_PREFERENCE = ["GRID", "MAIN", "SOLAR", "GENERATOR"];

const LOAD_STYLE = {
  label: "Power",
  color: "#3498db",
  icon: "/images/dashboard/transformer.svg",
};
const EXPORT_COLOR = "#2e7d32";
const IDLE_COLOR = "#cbd5e1";
const LOAD_NODE_ID = "__load__";

const normCat = (category) => (category || "").trim().toUpperCase();
const isVirtualNode = (node) => !!node.is_virtual || normCat(node.category) === "VIRTUAL";
const isSupplyCategory = (category) => {
  const cat = normCat(category);
  return !!cat && !CONSUMER_CATEGORIES.has(cat);
};

const titleCase = (value) =>
  value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Style resolver: known categories keep their identity, unknown ones get a stable
// slot in the fallback palette based on alphabetical order of what's present.
const buildStyleResolver = (categories) => {
  const unknown = categories
    .filter((category) => !CATEGORY_STYLES[category])
    .sort((a, b) => a.localeCompare(b));
  return (category) => {
    if (CATEGORY_STYLES[category]) return CATEGORY_STYLES[category];
    const index = unknown.indexOf(category);
    return {
      label: titleCase(category),
      color: FALLBACK_COLORS[(index < 0 ? 0 : index) % FALLBACK_COLORS.length],
      icon: null,
      signed: true,
    };
  };
};

const orderCategories = (categories, preference) => {
  const rank = (category) => {
    const index = preference.indexOf(category);
    return index < 0 ? preference.length : index;
  };
  return [...categories].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};

// ========== branch scoping ==========

// Mirrors the backend's find_branch_node(): locate the branch by name and use its
// subtree, so "mains for this branch" never picks up a shared main sitting above
// it. Falls back to the branch maps when no node carries the branch name.
const resolveBranchRoots = (tree, selectedBranch, branchOfNode) => {
  if (!tree) return [];
  if (!selectedBranch || selectedBranch === "All") return [tree];

  const target = selectedBranch.trim().toLowerCase();
  const matches = [];
  const findByName = (node) => {
    if ((node.machine_name || "").trim().toLowerCase() === target) {
      matches.push(node);
      return; // don't descend — the subtree is already covered
    }
    (node.children || []).forEach(findByName);
  };
  findByName(tree);
  if (matches.length > 0) return matches;

  // No node named after the branch: keep every node the branch map assigns to it.
  const pruned = [];
  const collectByMap = (node) => {
    if (node.machine_name && branchOfNode(node) === selectedBranch) {
      pruned.push(node);
      return;
    }
    (node.children || []).forEach(collectByMap);
  };
  collectByMap(tree);
  return pruned;
};

// ========== data derivation ==========

// Every supply meter inside the branch becomes its own node — all mains, all
// solar, all generators, however many there are.
const collectSourceNodes = (roots, readingFor) => {
  const sources = [];
  const seen = new Set();
  const walk = (node) => {
    const category = normCat(node.category);
    if (node.machine_name && isSupplyCategory(category) && !seen.has(node.machine_name)) {
      seen.add(node.machine_name);
      const reading = readingFor(node.machine_name);
      const raw = Number(reading?.power_kw) || 0;
      const signed = CATEGORY_STYLES[category]?.signed ?? true;
      sources.push({
        id: node.machine_name,
        name: node.machine_name,
        category,
        meterId: node.meter_id || null,
        power: signed ? raw : Math.abs(raw),
        signed,
        hasReading: !!reading,
        isStale: reading ? !!reading.is_stale : true,
      });
    }
    (node.children || []).forEach(walk);
  };
  roots.forEach(walk);
  return sources;
};

// Mirrors the backend's calculate_total_usage(): generation always counts, and
// consumption is read from the shallowest metered node on each path so a parent
// meter and its children are never double counted.
const computeBranchLoad = (roots, readingFor) => {
  // A stale meter is drawn as "offline", so it must not contribute to the total
  // either — otherwise the load node contradicts the source nodes.
  const powerOf = (node) => {
    const reading = readingFor(node.machine_name);
    if (!reading || reading.is_stale) return 0;
    return Number(reading.power_kw) || 0;
  };
  const counted = new Set();

  let generationTotal = 0;
  const walkGeneration = (node) => {
    const category = normCat(node.category);
    const style = CATEGORY_STYLES[category];
    if (node.machine_name && style && style.signed === false && !counted.has(node.machine_name)) {
      counted.add(node.machine_name);
      generationTotal += Math.abs(powerOf(node));
    }
    (node.children || []).forEach(walkGeneration);
  };
  roots.forEach(walkGeneration);

  const consumed = new Set();
  let consumptionTotal = 0;
  const walkConsumption = (node) => {
    const category = normCat(node.category);
    const isGeneration = CATEGORY_STYLES[category]?.signed === false;
    const isMeteredConsumer = node.machine_name && !isVirtualNode(node) && !isGeneration;
    if (isMeteredConsumer) {
      if (!consumed.has(node.machine_name)) {
        consumed.add(node.machine_name);
        consumptionTotal += powerOf(node);
      }
      return; // children are already inside this meter's reading
    }
    (node.children || []).forEach(walkConsumption);
  };
  roots.forEach(walkConsumption);

  return consumptionTotal + generationTotal;
};

// A meter with no reading, or a stale one, is drawn as "offline" with an idle
// line and contributes 0 to the branch total (see powerOf above) — so every
// other tally has to treat it the same way.
const isOffline = (node) => !node.hasReading || node.isStale;

const flowStateOf = (node, styleFor) => {
  const style = styleFor(node.category);
  if (isOffline(node)) {
    return { active: false, reverse: false, color: style.color };
  }
  if (node.signed) {
    if (node.power > MIN_FLOW_THRESHOLD) {
      return { active: true, reverse: false, color: style.color, lineColor: style.color };
    }
    // Negative on a signed source means the branch is pushing energy back out.
    // The ring keeps the category colour so the meter stays identifiable; only
    // the arrow and the value turn green.
    if (node.power < -MIN_FLOW_THRESHOLD) {
      return { active: true, reverse: true, color: style.color, lineColor: EXPORT_COLOR };
    }
    return { active: false, reverse: false, color: style.color, lineColor: style.color };
  }
  const threshold =
    node.category === "GENERATOR" ? GENSET_ACTIVE_THRESHOLD : MIN_FLOW_THRESHOLD;
  return {
    active: Math.abs(node.power) >= threshold,
    reverse: false,
    color: style.color,
    lineColor: style.color,
  };
};

// Magnitude only — direction is carried by the arrows / "export" suffix.
// Precision drops as the value grows so labels stay short enough for the circles.
const formatKw = (value) => {
  const magnitude = Math.abs(Number(value) || 0);
  const digits = magnitude >= 1000 ? 0 : magnitude >= 100 ? 1 : 2;
  return `${magnitude.toFixed(digits)} kW`;
};

// ========== canvas layout ==========

// Even *angular* steps bunch nodes together wherever an eccentric ellipse
// flattens out, so walk the ring and hand out even *arc-length* steps instead.
const spaceByArcLength = (startDeg, endDeg, rx, ry, count) => {
  if (count <= 0) return [];
  if (count === 1) return [(startDeg + endDeg) / 2];

  const SAMPLES = 720;
  const samples = [];
  let travelled = 0;
  let previous = null;
  for (let i = 0; i <= SAMPLES; i += 1) {
    const deg = startDeg + ((endDeg - startDeg) * i) / SAMPLES;
    const theta = (deg * Math.PI) / 180;
    const point = { x: rx * Math.cos(theta), y: -ry * Math.sin(theta) };
    if (previous) travelled += Math.hypot(point.x - previous.x, point.y - previous.y);
    samples.push({ deg, length: travelled });
    previous = point;
  }

  const angles = [];
  let cursor = 0;
  for (let i = 0; i < count; i += 1) {
    const target = (travelled * i) / (count - 1);
    while (cursor < samples.length - 1 && samples[cursor + 1].length < target) cursor += 1;
    const a = samples[cursor];
    const b = samples[Math.min(cursor + 1, samples.length - 1)];
    const t = b.length === a.length ? 0 : (target - a.length) / (b.length - a.length);
    angles.push(a.deg + (b.deg - a.deg) * t);
  }
  return angles;
};

// Below this a name is too clipped to be worth the stacked layout.
const MIN_STACKED_LABEL = 52;

// The ring only has to be wide enough to keep the sources apart and leave room
// for their labels. Past that, extra canvas just stretches every flow line — a
// 1300px card used to put a lone source 460px from the load. So the preferred
// radii are pulled in and capped.
//
// The cap has to relax as sources are added, though: at 20-plus sources the
// labels are what needs the room, not the circles, and a fixed cap made the ring
// so tight that neighbouring labels collided.
const ARC_TIGHTEN = 0.8;
const MAX_ARC_RX = 240;
const MAX_ARC_RY = 190;
const ARC_RELAX_FROM = 6; // sources beyond this each buy the ring more room
const ARC_RELAX_X = 5;
const ARC_RELAX_Y = 4;

// Radial labels sit outside the circle: gap, then the name and value rows. Nodes
// at the top and bottom of the ring get pushed out one extra step so that
// neighbours, which share the same text rows, don't overlap.
const LABEL_GAP = 9;
const LABEL_STEP = 22;
const LABEL_RESERVE = LABEL_GAP + LABEL_STEP + 27;

// Neighbours packed closer than this along the ring cannot both fit a label, so
// the ring splits into two radii — every other source sits a step closer to the
// load. Staggering beats growing the chart: it separates the labels *and* pulls
// half the sources in, where a uniform ring would have to push them all out.
const RING_CONGESTED = 100;
// Wide enough that a pulled-in source's label lands in the gap *between* the two
// rings. At a smaller stagger the label came out level with the outer circles
// beside it, which left it almost no width and clipped names to "10…".
const RING_STAGGER = 100;

const layoutRing = (nodes, width, height, stackedLabels) => {
  const cx = width / 2;
  let cy = height / 2;
  const count = nodes.length;
  // Upper bounds only — the two passes below shrink these if the ring turns out
  // to be too tight for them. A dense ring still gets a substantial circle: the
  // spacing check is what stops them touching, not this table.
  let loadRadius = count > 12 ? 50 : 54;
  let nodeRadius =
    count <= 4 ? 42 : count <= 8 ? 37 : count <= 12 ? 32 : count <= 18 ? 28 : 25;

  // A shallow arc reads like a one-line diagram; a near-full ring is the only
  // way to keep many sources apart. The shallow arc narrows on a narrow canvas:
  // its ±170° ends would otherwise sit level with the load, right where the
  // load's own label is.
  const span = !stackedLabels ? 330 : width < 560 ? 110 : width < 760 ? 140 : 160;

  // Room reserved outside the ring for the labels. Stacked labels sit directly
  // above/below the circle, so they need vertical room but little horizontal;
  // radial labels are the other way round.
  const relax = Math.max(0, count - ARC_RELAX_FROM);
  const preferredRx = Math.min(
    MAX_ARC_RX + relax * ARC_RELAX_X,
    (width / 2 - nodeRadius - (stackedLabels ? 60 : 118)) * ARC_TIGHTEN,
  );
  // Only the horizontal radius gets tightened here: the radial layout's ellipse
  // is already squeezed vertically by the canvas height, and shrinking it
  // further pushed the lowest sources right up against the load.
  const preferredRy = Math.min(
    MAX_ARC_RY + relax * ARC_RELAX_Y,
    height / 2 - nodeRadius - (stackedLabels ? 40 : LABEL_RESERVE),
  );
  // When the sources are crowded the ring has to grow past that reserve (labels
  // then truncate) — an overlapping ring is worse than a clipped name.
  const step = count > 1 ? Math.min(span / (count - 1), 180) : 360;
  const needed = count > 1 ? (nodeRadius + 5) / Math.sin((step * Math.PI) / 360) : 0;
  const hardRx = Math.max(60, width / 2 - nodeRadius - 20);
  const hardRy = Math.max(50, height / 2 - nodeRadius - 20);
  const rx = Math.min(hardRx, Math.max(60, preferredRx, needed));
  let ry = Math.min(hardRy, Math.max(50, preferredRy, needed));

  // With a shallow arc every source sits above the load, so drop the load toward
  // the bottom and let the arc use the whole canvas instead of only the top half.
  if (stackedLabels) {
    const topPad = nodeRadius + 27; // circle + name row
    const botPad = loadRadius + 26; // load circle + value row
    ry = Math.min(MAX_ARC_RY, Math.max(50, (height - topPad - botPad) * ARC_TIGHTEN));
    // The arc no longer fills the canvas, so centre the arc-plus-load block
    // rather than pinning it to the top.
    cy = topPad + ry + Math.max(0, height - topPad - ry - botPad) / 2;
  }

  const angles = spaceByArcLength(90 + span / 2, 90 - span / 2, rx, ry, count);

  // `inset` pulls every other source one step in toward the load.
  const placeAt = (inset) =>
    nodes.map((node, index) => {
      const theta = (angles[index] * Math.PI) / 180;
      const step = index % 2 === 1 ? inset : 0;
      const x = cx + (rx - step) * Math.cos(theta);
      const y = cy - (ry - step) * Math.sin(theta);
      const dx = x - cx;
      const dy = y - cy;
      const len = Math.hypot(dx, dy) || 1;
      return { ...node, x, y, ux: dx / len, uy: dy / len };
    });

  let placed = placeAt(0);

  // Stagger only once the ring is actually congested. A shallow arc of a few
  // sources has room for every label already, and alternating their distance
  // there would look arbitrary rather than deliberate.
  if (!stackedLabels && placed.length > 2) {
    const spacing = Math.min(
      ...placed.slice(1).map((node, i) => Math.hypot(node.x - placed[i].x, node.y - placed[i].y)),
    );
    if (spacing < RING_CONGESTED) placed = placeAt(RING_STAGGER);
  }

  // Labels for sources at the top and bottom of the ring are centred under the
  // circle, so neighbours there write into the same two text rows and collide
  // once the ring gets busy. Alternating them between two distances out gives
  // each row twice the horizontal room. The flanks are left alone: their labels
  // run sideways and the ring separates them vertically already.
  if (!stackedLabels) {
    let level = 0;
    placed.forEach((node) => {
      const centred = Math.abs(node.ux) <= 0.25;
      node.labelLevel = centred ? level : 0;
      level = centred && level === 0 ? 1 : 0;
    });
  }

  // Shrink the circles until neighbours (including the ring wrap) stop touching.
  const neighbours = [];
  for (let i = 1; i < placed.length; i += 1) neighbours.push([placed[i - 1], placed[i]]);
  if (span > 180 && placed.length > 2) neighbours.push([placed[placed.length - 1], placed[0]]);
  neighbours.forEach(([a, b]) => {
    const gap = Math.hypot(b.x - a.x, b.y - a.y);
    nodeRadius = Math.min(nodeRadius, Math.max(11, (gap - 10) / 2));
  });

  // On a narrow canvas the ring can collapse onto the load node — shrink both
  // circles rather than letting them overlap. Stacked labels sit between the two
  // circles, so they need a much wider corridor than radial ones.
  // How much combined radius each source can afford before it clashes with the
  // load. With stacked labels a source sitting almost level with the load needs
  // *vertical* room for the two label rows between them; one that is well clear
  // horizontally only needs the circles not to touch.
  const budget = placed.reduce((min, node) => {
    const dx = Math.abs(node.x - cx);
    const dy = Math.abs(node.y - cy);
    const afford =
      stackedLabels && dx < 68 ? dy - 42 : Math.hypot(dx, dy) - 16;
    return Math.min(min, afford);
  }, Infinity);
  if (Number.isFinite(budget) && nodeRadius + loadRadius > budget) {
    const scale = Math.max(0.4, budget / (nodeRadius + loadRadius));
    nodeRadius = Math.max(11, nodeRadius * scale);
    loadRadius = Math.max(22, loadRadius * scale);
  }

  // Stacked labels are centred on the node, so clamp each one to the horizontal
  // room actually available. Only labels whose *rows* overlap vertically can
  // clash, so a neighbour sitting far higher or lower costs nothing — which is
  // what lets a narrow canvas still show full machine names.
  if (stackedLabels) {
    const LOAD_LABEL_HALF = 42;
    const rowsOf = (y, r) => [
      [y - r - 22, y - r - 8], // name row
      [y + r + 6, y + r + 20], // value row
    ];
    const overlaps = (a, b) => a[0] < b[1] && b[0] < a[1];
    const loadRows = rowsOf(cy, loadRadius);

    placed.forEach((node) => {
      const myRows = rowsOf(node.y, nodeRadius);
      let room = Math.min(node.x, width - node.x) * 2 - 12;

      placed.forEach((other) => {
        if (other === node) return;
        const otherRows = rowsOf(other.y, nodeRadius);
        if (myRows.some((m) => otherRows.some((o) => overlaps(m, o)))) {
          room = Math.min(room, Math.abs(other.x - node.x) - 8);
        }
      });

      if (myRows.some((m) => loadRows.some((o) => overlaps(m, o)))) {
        room = Math.min(room, 2 * (Math.abs(cx - node.x) - LOAD_LABEL_HALF - 6));
      }

      // Raw room, no floor — buildLayout uses it to decide whether the stacked
      // layout is viable at all.
      node.labelWidth = room;
    });
  }

  // Radial labels for sources at the top and bottom of the ring are centred
  // under the circle, so what limits their width is a neighbouring circle
  // sitting sideways at the same height — which is easy to hit once the ring is
  // staggered, because a pulled-in source's label reaches out under the two
  // sources beside it. Flank labels run outward, away from the ring, and need no
  // limit beyond the canvas edge.
  if (!stackedLabels) {
    placed.forEach((node) => {
      const out = nodeRadius + LABEL_GAP + (node.labelLevel || 0) * LABEL_STEP;
      const anchorX = node.x + node.ux * out;
      const anchorY = node.y + node.uy * out;
      // Only sources well out on the flanks get sideways labels. Anything nearer
      // the top or bottom of the ring writes underneath its circle instead:
      // sideways there runs straight into the next circle along.
      const centred = Math.abs(node.ux) <= 0.55;
      const towardsRight = node.ux > 0;
      node.labelAlign = centred ? "center" : towardsRight ? "left" : "right";

      let room = Infinity;
      placed.forEach((other) => {
        if (other === node) return;
        // Only a circle at roughly the label's own height can be in the way.
        if (Math.abs(other.y - anchorY) > nodeRadius + 26) return;
        const clear = Math.abs(other.x - anchorX) - nodeRadius - 6;
        if (centred) room = Math.min(room, clear * 2); // grows both ways
        // Otherwise only a circle on the side the text runs towards matters.
        else if (towardsRight === (other.x > anchorX)) {
          room = Math.min(room, clear);
        }
      });
      node.labelWidth = room;
    });
  }

  return { cx, cy, loadRadius, nodeRadius, stackedLabels, nodes: placed };
};

const buildLayout = (nodes, width, height) => {
  // Prefer the familiar name-above / value-below layout, but only when every
  // label has room to be readable; otherwise fall back to radial labels.
  if (nodes.length > 0 && nodes.length <= 7) {
    const stacked = layoutRing(nodes, width, height, true);
    if (stacked.nodes.every((node) => node.labelWidth >= MIN_STACKED_LABEL)) {
      return stacked;
    }
  }
  return layoutRing(nodes, width, height, false);
};

// Labels sit on top of the flow lines, so every one gets a white outline.
const drawLabel = (ctx, text, x, y, { font, color, align = "center", baseline = "middle" }) => {
  if (!text) return;
  ctx.save();
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  ctx.lineJoin = "round";
  ctx.lineWidth = 3.5;
  ctx.strokeStyle = "rgba(255,255,255,0.92)";
  ctx.strokeText(text, x, y);
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
  ctx.restore();
};

const fitText = (ctx, text, maxWidth) => {
  if (maxWidth <= 8) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  let clipped = text;
  while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
};

// Solid glyph in the node colour on a white disc, so the icon reads clearly
// instead of sitting behind the flow lines as a watermark.
const drawNodeFace = (ctx, image, x, y, radius, color, dim, fallbackLabel, fade = 1) => {
  const hasIcon = !!(image && image.complete && image.naturalWidth);

  if (hasIcon) {
    ctx.save();
    try {
      ctx.beginPath();
      ctx.arc(x, y, radius - 1, 0, 2 * Math.PI);
      ctx.clip();

      const pad = radius * 0.34;
      const box = (radius - pad) * 2;
      ctx.drawImage(image, x - radius + pad, y - radius + pad, box, box);
      // Recolour the glyph to the node colour, keeping only its silhouette.
      ctx.globalCompositeOperation = "source-in";
      ctx.globalAlpha = (dim ? 0.45 : 1) * fade;
      ctx.fillStyle = color;
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      // Paint the rest of the disc white *behind* the glyph so flow lines don't
      // show through and the icon keeps its contrast on any background.
      ctx.globalCompositeOperation = "destination-over";
      ctx.globalAlpha = fade;
      ctx.fillStyle = "#fff";
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    } catch {
      // SVGs without an intrinsic size can throw here — the ring alone is enough.
    } finally {
      ctx.restore();
    }
    return;
  }

  // No icon for this category (a supply type we don't ship art for): white disc
  // with the category initial, so the node still reads as something.
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.beginPath();
  ctx.arc(x, y, radius - 1, 0, 2 * Math.PI);
  ctx.fillStyle = "#fff";
  ctx.fill();
  if (fallbackLabel) {
    ctx.globalAlpha = (dim ? 0.45 : 1) * fade;
    ctx.fillStyle = color;
    ctx.font = `700 ${Math.max(10, Math.round(radius * 0.92))}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(fallbackLabel, x, y + radius * 0.04);
  }
  ctx.restore();
};

// A live line is a faint solid track carrying one small group of arrowheads,
// travelling close together from source to load. Arrows spread along the whole
// line read as busy; a single group is one thing to follow per line.
const ARROW_LEN = 9; // tip-to-base length
const ARROW_HALF = 3.6; // half the width at the base
const TRAVEL_ARROWS = 3; // arrows in the group
const ARROW_GAP = 15; // px between them — close, so they read as one group
const ARROW_FADE = 18; // px of fade in/out at each end of the line
// One full source → load pass, for the biggest source on screen. Slow on
// purpose: this is an ambient view of where power is coming from, not a gauge.
const ARROW_CYCLE_MS = 6500;

// Speed carries the reading. The busiest source in view runs at full speed and
// quieter ones drift, so a glance shows where the power is actually coming from
// before you read a number. The map is compressed and floored deliberately: on a
// linear one, a 25 kW string next to a 390 kW main looked broken rather than
// quiet, and a source at 0 would never move at all.
const ARROW_SPEED_FLOOR = 0.4;

// Hovering a source fades the rest back to this share of their opacity, eased
// over FOCUS_EASE_MS so the change is a settle rather than a flick.
const FOCUS_FADE = 0.74;
const FOCUS_EASE_MS = 170;
const flowSpeed = (power, maxPower) => {
  if (!(maxPower > 0)) return 1;
  const share = Math.min(1, Math.abs(power) / maxPower);
  return ARROW_SPEED_FLOOR + (1 - ARROW_SPEED_FLOOR) * Math.sqrt(share);
};

const drawFlow = (ctx, from, to, color, phase, animate, fade = 1) => {
  const totalLen = Math.hypot(to.x - from.x, to.y - from.y);
  if (totalLen < 1) return;

  // Unit vector along the line, and its perpendicular for the arrow's wings.
  const ux = (to.x - from.x) / totalLen;
  const uy = (to.y - from.y) / totalLen;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = "round";

  // Track, always visible so the topology stays readable.
  ctx.lineWidth = 4;
  ctx.globalAlpha = (animate ? 0.2 : 0.14) * fade;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();

  // Arrows point the way the power is going: source → load, or load → source on
  // export, because the caller swaps the two ends for a reversed flow.
  if (animate) {
    // Travel is inset by half an arrow at each end so no arrow overlaps a node.
    const travelLen = Math.max(totalLen - ARROW_LEN, 1);
    // The phase spans the line *plus* the group's own length, so the group walks
    // off the load end and back in at the source rather than wrapping mid-line.
    const trailLen = (TRAVEL_ARROWS - 1) * ARROW_GAP;
    const head = phase * (travelLen + trailLen);
    for (let i = 0; i < TRAVEL_ARROWS; i += 1) {
      const offset = head - i * ARROW_GAP;
      if (offset < 0 || offset > travelLen) continue;
      // Soften the arrows at the two ends so the group eases in and out instead
      // of popping. The fade keeps a floor: taken to zero it left the line empty
      // for an instant on every pass, which read as a blink. The tail is tapered
      // as well, so the group shows which way it is going.
      const ease = Math.min(1, offset / ARROW_FADE, (travelLen - offset) / ARROW_FADE);
      const alpha = (0.35 + 0.65 * ease) * (1 - i * 0.22) * fade;

      const dist = ARROW_LEN / 2 + offset;
      const x = from.x + ux * dist;
      const y = from.y + uy * dist;
      const baseX = x - ux * (ARROW_LEN / 2);
      const baseY = y - uy * (ARROW_LEN / 2);

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.moveTo(x + ux * (ARROW_LEN / 2), y + uy * (ARROW_LEN / 2)); // tip
      ctx.lineTo(baseX - uy * ARROW_HALF, baseY + ux * ARROW_HALF);
      ctx.lineTo(baseX + uy * ARROW_HALF, baseY - ux * ARROW_HALF);
      ctx.closePath();
      ctx.fill();
    }
  }

  ctx.restore();
};

// Draws one frame and returns the layout plus whether anything is flowing.
// Module level (not inside the component) so it can be rendered and inspected
// outside React.
export const drawFrame = (ctx, options) => {
  const {
    width,
    height,
    nodes,
    loadTotal,
    styleFor: resolveStyle,
    icons = {},
    hoverId = null,
    phase = 0,
    // id → 0..1 position of that line's arrow group. Per line, because each one
    // runs at its own speed; `phase` is the fallback for a one-off render.
    phases = null,
    // { id, amount } — the source being hovered and how far the fade has eased
    // in. Everything else recedes, which is the only way to follow one line out
    // of a ring of twenty-odd.
    focus = null,
  } = options;

  ctx.clearRect(0, 0, width, height);

  const layout = buildLayout(nodes, width, height);
  const { cx, cy, loadRadius, nodeRadius, stackedLabels } = layout;
  let anyFlow = false;

  const focusId = focus && focus.amount > 0 ? focus.id : null;
  const fadeFor = (id) => (focusId && focusId !== id ? 1 - FOCUS_FADE * focus.amount : 1);

  // Flows first so the circles sit on top of the lines.
  layout.nodes.forEach((node) => {
    const flow = flowStateOf(node, resolveStyle);
    if (flow.active) anyFlow = true;
    const edge = { x: node.x - node.ux * nodeRadius, y: node.y - node.uy * nodeRadius };
    const loadEdge = { x: cx + node.ux * loadRadius, y: cy + node.uy * loadRadius };
    const from = flow.reverse ? loadEdge : edge;
    const to = flow.reverse ? edge : loadEdge;
    const nodePhase = phases?.get(node.id) ?? phase;
    drawFlow(
      ctx,
      from,
      to,
      flow.active ? flow.lineColor : IDLE_COLOR,
      nodePhase,
      flow.active,
      fadeFor(node.id),
    );
  });

  // Load node at the centre.
  drawNodeFace(ctx, icons[LOAD_NODE_ID], cx, cy, loadRadius, LOAD_STYLE.color, false);
  ctx.save();
  if (hoverId === LOAD_NODE_ID) {
    ctx.shadowColor = LOAD_STYLE.color;
    ctx.shadowBlur = 14;
  }
  ctx.strokeStyle = LOAD_STYLE.color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, cy, loadRadius, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.restore();

  drawLabel(ctx, LOAD_STYLE.label, cx, cy - loadRadius - 10, {
    font: "600 13px Arial",
    color: "#0f172a",
    baseline: "alphabetic",
  });
  drawLabel(ctx, formatKw(loadTotal), cx, cy + loadRadius + 8, {
    font: "700 13px Arial",
    color: LOAD_STYLE.color,
    baseline: "top",
  });

  // Source nodes.
  layout.nodes.forEach((node) => {
    const flow = flowStateOf(node, resolveStyle);
    const offline = isOffline(node);
    const ringColor = offline ? IDLE_COLOR : flow.color;

    const fade = fadeFor(node.id);
    drawNodeFace(
      ctx,
      icons[node.category],
      node.x,
      node.y,
      nodeRadius,
      ringColor,
      offline,
      (resolveStyle(node.category).label || node.category).charAt(0).toUpperCase(),
      fade,
    );

    ctx.save();
    if (hoverId === node.id) {
      ctx.shadowColor = ringColor;
      ctx.shadowBlur = 14;
    }
    ctx.globalAlpha = fade;
    ctx.strokeStyle = ringColor;
    ctx.lineWidth = hoverId === node.id ? 5 : 4;
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();

    const valueLabel = offline
      ? "offline"
      : flow.reverse
        ? `${formatKw(node.power)} export`
        : formatKw(node.power);
    const nameFont = `${hoverId === node.id ? "700" : "600"} 12px Arial`;
    const nameColor = offline ? "#94a3b8" : "#0f172a";
    const valueColor = offline ? "#94a3b8" : flow.lineColor;

    // drawLabel saves and restores, but never sets globalAlpha itself, so this
    // carries the fade into both rows.
    ctx.save();
    ctx.globalAlpha = fade;

    if (stackedLabels) {
      // Name above the circle, value below — the familiar one-line look.
      ctx.font = nameFont;
      drawLabel(ctx, fitText(ctx, node.name, node.labelWidth), node.x, node.y - nodeRadius - 9, {
        font: nameFont,
        color: nameColor,
        baseline: "alphabetic",
      });
      ctx.font = "700 12px Arial";
      drawLabel(ctx, fitText(ctx, valueLabel, node.labelWidth), node.x, node.y + nodeRadius + 7, {
        font: "700 12px Arial",
        color: valueColor,
        baseline: "top",
      });
      ctx.restore();
      return;
    }

    // Crowded ring: two lines radiating away from the centre.
    const anchorOut = nodeRadius + LABEL_GAP + (node.labelLevel || 0) * LABEL_STEP;
    const anchorX = node.x + node.ux * anchorOut;
    const anchorY = node.y + node.uy * anchorOut;
    // Decided in layoutRing, alongside the room measured for that direction.
    const align = node.labelAlign || "center";
    let nameY = anchorY - 6;
    let valueY = anchorY + 7;
    if (node.uy < -0.25) {
      nameY = anchorY - 15;
      valueY = anchorY - 3;
    } else if (node.uy > 0.25) {
      nameY = anchorY + 4;
      valueY = anchorY + 16;
    }

    // The canvas edge is a hard bound; the room measured against neighbouring
    // circles narrows it further.
    const edgeRoom =
      align === "left"
        ? width - anchorX - 6
        : align === "right"
          ? anchorX - 6
          : Math.min(anchorX, width - anchorX) * 2 - 12;
    const available = Math.min(edgeRoom, node.labelWidth ?? Infinity);
    // A clipped reading is useless, so the value row keeps a floor wide enough
    // for "999.9 kW" even where the name above it has to give way — but never
    // past the edge of the canvas.
    const valueRoom = Math.min(edgeRoom, Math.max(available, 58));

    ctx.font = nameFont;
    drawLabel(ctx, fitText(ctx, node.name, available), anchorX, nameY, {
      font: nameFont,
      color: nameColor,
      align,
    });
    ctx.font = "700 12px Arial";
    drawLabel(ctx, fitText(ctx, valueLabel, valueRoom), anchorX, valueY, {
      font: "700 12px Arial",
      color: valueColor,
      align,
    });
    ctx.restore();
  });

  return { layout, anyFlow };
};

// ========== component ==========

const PowerDistributionFlow = ({
  hierarchyTree,
  latestData,
  loading = false,
  selectedBranch = "All",
  machineToBranchMap = {},
  meterIdToBranchMap = {},
}) => {
  // null means "all sources"; a Set is only ever set by isolating a node on the
  // canvas. Defaulting to null means newly connected meters appear automatically.
  const [selectedIds, setSelectedIds] = useState(null);
  const [pointerOnNode, setPointerOnNode] = useState(false);
  // Measured canvas width — feeds the height so narrow layouts get more room.
  const [canvasWidth, setCanvasWidth] = useState(0);

  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const sizeRef = useRef({ width: 0, height: 0 });
  const layoutRef = useRef(null);
  const hoverRef = useRef(null);
  // meter id → 0..1 position of that line's arrow group, plus the timestamp of
  // the last frame so phases advance by real time, not by frame count.
  const phasesRef = useRef(new Map());
  const lastFrameRef = useRef(0);
  // Which source is being read, and how far the "fade the rest back" has eased
  // in. Held separately from hoverRef because the fade-out has to keep animating
  // on the node the pointer just left.
  const focusRef = useRef({ id: null, amount: 0 });
  const drawRef = useRef({ nodes: [], loadTotal: 0, styleFor: () => LOAD_STYLE });
  const iconsRef = useRef({});

  const branchOfNode = useCallback(
    (node) =>
      machineToBranchMap[node.machine_name] ||
      meterIdToBranchMap[String(node.meter_id)] ||
      null,
    [machineToBranchMap, meterIdToBranchMap],
  );

  const branchRoots = useMemo(
    () => resolveBranchRoots(hierarchyTree, selectedBranch, branchOfNode),
    [hierarchyTree, selectedBranch, branchOfNode],
  );

  const readingFor = useMemo(() => {
    const map = new Map();
    (latestData?.readings || []).forEach((reading) => {
      if (reading?.machine_name) map.set(reading.machine_name, reading);
    });
    return (machineName) => map.get(machineName) || null;
  }, [latestData]);

  const sources = useMemo(
    () => collectSourceNodes(branchRoots, readingFor),
    [branchRoots, readingFor],
  );

  const loadTotal = useMemo(
    () => computeBranchLoad(branchRoots, readingFor),
    [branchRoots, readingFor],
  );

  // Categories are whatever the branch actually contains.
  const presentCategories = useMemo(
    () => Array.from(new Set(sources.map((source) => source.category))),
    [sources],
  );

  const styleFor = useMemo(() => buildStyleResolver(presentCategories), [presentCategories]);

  useEffect(() => {
    const wanted = { [LOAD_NODE_ID]: LOAD_STYLE.icon };
    presentCategories.forEach((category) => {
      const icon = styleFor(category).icon;
      if (icon) wanted[category] = icon;
    });
    const images = { ...iconsRef.current };
    Object.entries(wanted).forEach(([key, src]) => {
      if (images[key]?.dataset?.src === src) return;
      const img = new Image();
      img.dataset.src = src;
      img.src = src;
      images[key] = img;
    });
    iconsRef.current = images;
  }, [presentCategories, styleFor]);

  const isSelected = useCallback(
    (id) => selectedIds === null || selectedIds.has(id),
    [selectedIds],
  );

  const visibleSources = useMemo(
    () => sources.filter((source) => isSelected(source.id)),
    [sources, isSelected],
  );

  const allSelected = selectedIds === null || visibleSources.length === sources.length;

  // Solar left → mains top → genset right, tree order preserved inside a category.
  const arcNodes = useMemo(() => {
    const ordered = [];
    orderCategories(presentCategories, ARC_PREFERENCE).forEach((category) => {
      visibleSources.forEach((source) => {
        if (source.category === category) ordered.push(source);
      });
    });
    return ordered;
  }, [visibleSources, presentCategories]);

  const groups = useMemo(
    () =>
      orderCategories(presentCategories, PANEL_PREFERENCE).map((category) => ({
        category,
        items: sources.filter((source) => source.category === category),
      })),
    [sources, presentCategories],
  );

  // Offline sources are skipped: their last reading is no longer live, the node
  // says "offline", and the branch total already excludes them — counting them
  // here made Supply disagree with the total in the same summary row.
  const selectedSupply = useMemo(
    () =>
      visibleSources.reduce(
        (sum, source) =>
          isOffline(source) ? sum : sum + (source.signed ? source.power : Math.abs(source.power)),
        0,
      ),
    [visibleSources],
  );

  const canvasHeight = useMemo(() => {
    const count = arcNodes.length;
    // Up to six sources use the shallow stacked arc, whose radius is capped at
    // MAX_ARC_RY — extra height there is pure padding. From seven on, the layout
    // is a near-full ring that needs the vertical room, including LABEL_RESERVE
    // for the radial labels below the lowest source.
    const base =
      count <= 6 ? 380 : count <= 8 ? 470 : Math.min(790, 470 + (count - 8) * 26);
    // A narrow canvas can't spread the arc sideways, so give it height instead.
    const narrowBoost =
      canvasWidth > 0 && canvasWidth < 700 ? Math.round((700 - canvasWidth) * 0.35) : 0;
    return Math.min(840, base + narrowBoost);
  }, [arcNodes.length, canvasWidth]);

  // Single source of truth for "is the canvas on screen" — the sizing effect
  // depends on it, so it must match the JSX branch below exactly.
  const showCanvas = sources.length > 0;

  drawRef.current = { nodes: arcNodes, loadTotal, styleFor };

  // Reset to all sources when the branch changes — an isolated machine name
  // usually doesn't exist in the new branch.
  useEffect(() => {
    setSelectedIds(null);
  }, [selectedBranch]);

  const selectAll = useCallback(() => setSelectedIds(null), []);
  const selectOnly = useCallback((ids) => setSelectedIds(new Set(ids)), []);

  const isolateFromCanvas = useCallback(
    (id) => {
      // Clicking the isolated node (or the load) returns to the full view.
      if (id === LOAD_NODE_ID) {
        selectAll();
        return;
      }
      if (selectedIds && selectedIds.size === 1 && selectedIds.has(id)) selectAll();
      else selectOnly([id]);
    },
    [selectedIds, selectAll, selectOnly],
  );

  // ---- canvas sizing ----
  // Kept in a callback so the effect below can re-run it: the backing store and
  // sizeRef must always agree, or the draw loop paints at the wrong scale.
  const applySize = useCallback(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    const width = wrapper.clientWidth;
    const height = wrapper.clientHeight;
    if (!width || !height) return;
    sizeRef.current = { width, height };
    setCanvasWidth(width);
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }, []);

  // `showCanvas` is a dependency because the loading and no-sources states
  // replace the canvas entirely: switching to a branch with no sources unmounts
  // it, and it remounts with a default 300×150 buffer that only this effect can
  // correct. Without it, coming back from such a branch drew nothing.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    applySize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", applySize);
      return () => window.removeEventListener("resize", applySize);
    }
    const observer = new ResizeObserver(applySize);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [applySize, canvasHeight, showCanvas]);

  // ---- render loop (reads live values from refs, so it never restarts) ----
  useEffect(() => {
    let frameId = 0;

    const render = (timestamp) => {
      // Time-based so the drift is the same speed on a 60Hz and a 120Hz screen.
      // Capped so a backgrounded tab doesn't resume with a jump.
      const previous = lastFrameRef.current;
      lastFrameRef.current = timestamp;
      const elapsed = previous ? Math.min(timestamp - previous, 100) : 0;

      // Ease the read-one-source fade. Hovering the load means "everything", so
      // it counts as no focus. Moving straight from one source to another keeps
      // the current amount, so neighbours don't flash back to full brightness.
      const focus = focusRef.current;
      const target =
        hoverRef.current && hoverRef.current !== LOAD_NODE_ID ? hoverRef.current : null;
      if (target) focus.id = target;
      const step = elapsed / FOCUS_EASE_MS;
      focus.amount = target
        ? Math.min(1, focus.amount + step)
        : Math.max(0, focus.amount - step);
      if (focus.amount === 0) focus.id = null;

      const canvas = canvasRef.current;
      const { width, height } = sizeRef.current;
      // getContext returns null where 2d canvas is unavailable (headless, blocked).
      const ctx = canvas && width && height ? canvas.getContext("2d") : null;
      if (ctx) {
        const dpr = window.devicePixelRatio || 1;
        ctx.save();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const { nodes, loadTotal, styleFor: resolveStyle } = drawRef.current;
        const { layout, anyFlow } = drawFrame(ctx, {
          width,
          height,
          nodes,
          loadTotal,
          styleFor: resolveStyle,
          icons: iconsRef.current,
          hoverId: hoverRef.current,
          phases: phasesRef.current,
          focus,
        });
        layoutRef.current = layout;

        if (anyFlow) {
          // Advance each line by its own speed, integrating rather than deriving
          // the phase from absolute time: a data refresh changes the speeds, and
          // recomputing would teleport every arrow group on that frame.
          const maxPower = nodes.reduce((max, node) => Math.max(max, Math.abs(node.power)), 0);
          const next = new Map();
          nodes.forEach((node, index) => {
            // Lines start out of step so 20-plus of them don't pulse in unison.
            const from = phasesRef.current.get(node.id) ?? (index * 0.37) % 1;
            const speed = flowSpeed(node.power, maxPower);
            next.set(node.id, (from + (elapsed * speed) / ARROW_CYCLE_MS) % 1);
          });
          phasesRef.current = next; // rebuilt, so meters that go away don't pile up
        }
        ctx.restore();
      }
      frameId = requestAnimationFrame(render);
    };

    frameId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frameId);
  }, []);

  // ---- canvas interaction ----
  const hitTest = useCallback((event) => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    if (!canvas || !layout) return null;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const hit = layout.nodes.find(
      (node) => Math.hypot(node.x - x, node.y - y) <= layout.nodeRadius + 4,
    );
    if (hit) return hit.id;
    if (Math.hypot(layout.cx - x, layout.cy - y) <= layout.loadRadius + 4) return LOAD_NODE_ID;
    return null;
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      const id = hitTest(event);
      hoverRef.current = id;
      setPointerOnNode(!!id);
    },
    [hitTest],
  );

  const handleMouseLeave = useCallback(() => {
    hoverRef.current = null;
    setPointerOnNode(false);
  }, []);

  const handleClick = useCallback(
    (event) => {
      const id = hitTest(event);
      if (id) isolateFromCanvas(id);
    },
    [hitTest, isolateFromCanvas],
  );

  // ---- render ----
  const branchText = selectedBranch === "All" ? "all branches" : selectedBranch;
  const countsLabel = groups
    .map((group) => `${group.items.length} ${styleFor(group.category).label}`)
    .join(" · ");

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Power Distribution</h2>
          <p style={styles.subtitle}>
            {sources.length > 0
              ? `Live flow from every main and source in ${branchText} — ${countsLabel}`
              : `Live power flow between sources and load — ${branchText}`}
          </p>
        </div>
      </div>

      {loading && !showCanvas ? (
        <div style={styles.empty}>Loading live distribution…</div>
      ) : !showCanvas ? (
        <div style={styles.empty}>
          No mains, solar or generator meters found in{" "}
          {selectedBranch === "All" ? "this hierarchy" : selectedBranch}.
        </div>
      ) : (
        <div>
          <div
            ref={wrapperRef}
            style={{
              ...styles.canvasWrapper,
              height: canvasHeight,
              cursor: pointerOnNode ? "pointer" : "default",
            }}
          >
            <canvas
              ref={canvasRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
            {arcNodes.length === 0 && (
              <div style={styles.canvasOverlay}>
                Click the Power node to show all sources again.
              </div>
            )}
          </div>
          <div style={styles.summary}>
            <span>
              {allSelected ? (
                <>
                  <strong>{sources.length}</strong> sources
                </>
              ) : (
                <>
                  <strong>{visibleSources.length}</strong> of{" "}
                  <strong>{sources.length}</strong> sources
                </>
              )}
            </span>
            <span>
              Supply <strong>{formatKw(selectedSupply)}</strong>
              {selectedSupply < -MIN_FLOW_THRESHOLD ? " (net export)" : ""}
            </span>
            <span>
              {selectedBranch === "All" ? "Total" : selectedBranch} power{" "}
              <strong>{formatKw(loadTotal)}</strong>
            </span>
            <span style={styles.hint}>
              Click a node to isolate it · click again for all
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: 20,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  title: { margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#0f172a" },
  subtitle: { margin: 0, color: "#64748b", fontSize: 14 },
  canvasWrapper: {
    position: "relative",
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    overflow: "hidden",
  },
  canvasOverlay: {
    position: "absolute",
    top: 14,
    left: "50%",
    transform: "translateX(-50%)",
    maxWidth: "88%",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    color: "#64748b",
    fontSize: 13,
    textAlign: "center",
    padding: "8px 16px",
    pointerEvents: "none",
  },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 10,
    fontSize: 12,
    color: "#64748b",
  },
  hint: { marginLeft: "auto", fontStyle: "italic" },
  empty: {
    padding: 40,
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
    border: "1px dashed #e2e8f0",
    borderRadius: 12,
  },
};

export default PowerDistributionFlow;
